import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CountrySelect } from '@/components/CountrySelect';
import type { PlayersController } from '@/pages/players/usePlayersController';
import { ratingFieldLabel, externalIdFieldLabel } from '@/lib/game-labels';

interface NewPlayerRowProps {
  ctrl: PlayersController;
}

export function NewPlayerRow({ ctrl }: NewPlayerRowProps) {
  const { t } = useTranslation('director');
  const { t: tc } = useTranslation('common');
  const {
    showRating,
    showExternalId,
    gameConfig,
    newFirstName,
    setNewFirstName,
    newLastName,
    setNewLastName,
    newRating,
    setNewRating,
    newFideId,
    setNewFideId,
    newCity,
    setNewCity,
    newCountry,
    setNewCountry,
    newClub,
    setNewClub,
    addingPlayer,
    handleAddPlayer,
    newRowMatchesExisting,
    handleNewRowKeyDown,
    handleNewFideIdKeyDown,
    newFirstNameRef,
  } = ctrl;

  return (
    <tr
      data-testid="add-player-row"
      className={newRowMatchesExisting ? 'bg-yellow-50' : 'bg-muted/20'}
    >
      <td className="w-8 px-2 py-1"></td>
      <td className="w-6 px-1 py-1">
        {newRowMatchesExisting && (
          <span title={t('players_page.duplicate_title')} data-testid="duplicate-flag-new">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
          </span>
        )}
      </td>
      <td className="w-32 px-1 py-1">
        <Input
          ref={newFirstNameRef}
          data-testid="new-player-firstName"
          className="h-7 text-sm"
          placeholder={t('players_page.new_row.first_name_placeholder')}
          value={newFirstName}
          onChange={(e) => setNewFirstName(e.target.value)}
          onKeyDown={handleNewRowKeyDown}
        />
      </td>
      <td className="w-32 px-1 py-1">
        <Input
          data-testid="new-player-lastName"
          className="h-7 text-sm"
          placeholder={t('players_page.new_row.last_name_placeholder')}
          value={newLastName}
          onChange={(e) => setNewLastName(e.target.value)}
          onKeyDown={handleNewRowKeyDown}
        />
      </td>
      {showRating && (
        <td className="w-20 px-1 py-1">
          <Input
            data-testid="new-player-rating"
            className="h-7 text-sm"
            type="number"
            placeholder={ratingFieldLabel(gameConfig, tc)}
            value={newRating}
            onChange={(e) => setNewRating(e.target.value)}
            onKeyDown={handleNewRowKeyDown}
          />
        </td>
      )}
      {showExternalId && (
        <td className="w-24 px-1 py-1">
          {/* Story 098 — Enter here looks the FIDE ID up and prefills the
              row, so a player can be registered from just their ID. */}
          <Input
            data-testid="new-player-fideId"
            className="h-7 text-sm"
            type="number"
            placeholder={externalIdFieldLabel(gameConfig, tc)}
            value={newFideId}
            onChange={(e) => setNewFideId(e.target.value)}
            onKeyDown={handleNewFideIdKeyDown}
          />
        </td>
      )}
      <td className="w-24 px-1 py-1">
        <Input
          data-testid="new-player-city"
          className="h-7 text-sm"
          placeholder={t('players_page.new_row.city_placeholder')}
          value={newCity}
          onChange={(e) => setNewCity(e.target.value)}
          onKeyDown={handleNewRowKeyDown}
        />
      </td>
      <td className="w-24 px-1 py-1">
        <CountrySelect
          data-testid="new-player-country"
          value={newCountry}
          onChange={setNewCountry}
        />
      </td>
      <td className="w-28 px-1 py-1">
        <Input
          data-testid="new-player-club"
          className="h-7 text-sm"
          placeholder={t('players_page.new_row.club_placeholder')}
          value={newClub}
          onChange={(e) => setNewClub(e.target.value)}
          onKeyDown={handleNewRowKeyDown}
        />
      </td>
      <td className="w-52 px-2 py-1"></td>
      <td className="w-16 px-2 py-1"></td>
      <td className="w-10 px-2 py-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          data-testid="add-player-button"
          disabled={addingPlayer || !newFirstName.trim() || !newLastName.trim()}
          onClick={handleAddPlayer}
        >
          +
        </Button>
      </td>
    </tr>
  );
}
